/**
 * Offline Socket.IO Fallback
 * Uses BroadcastChannel and localStorage for cross-tab communication when backend is down
 */

class OfflineSocket {
  constructor() {
    this.isOnline = true;
    this.listeners = new Map(); // event -> Set of callbacks
    this.broadcastChannel = null;
    this.socketId = this.generateSocketId();
    
    // Try to use BroadcastChannel (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('offline-socket');
      this.broadcastChannel.onmessage = (event) => {
        this.handleMessage(event.data);
      };
      console.log('✅ OfflineSocket: BroadcastChannel initialized');
    } else {
      // Fallback to localStorage events (older browsers)
      window.addEventListener('storage', (event) => {
        if (event.key === 'offline-socket-message' && event.newValue) {
          try {
            const data = JSON.parse(event.newValue);
            this.handleMessage(data);
          } catch (err) {
            console.error('OfflineSocket: Error parsing localStorage message', err);
          }
        }
      });
      console.log('✅ OfflineSocket: localStorage fallback initialized');
    }
  }

  generateSocketId() {
    return `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Register event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    console.log(`📡 OfflineSocket: Registered listener for "${event}"`);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit event to other tabs/windows AND local listeners (when offline)
   */
  emit(event, data) {
    const message = {
      event,
      data,
      socketId: this.socketId,
      timestamp: Date.now()
    };

    // Trigger local listeners immediately (same tab)
    // This is needed because BroadcastChannel doesn't trigger in the same tab
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
          console.log(`📥 OfflineSocket: Triggered local listener for "${event}"`, data);
        } catch (err) {
          console.error(`OfflineSocket: Error in local "${event}" callback`, err);
        }
      });
    }

    // Broadcast to other tabs
    if (this.broadcastChannel) {
      // Use BroadcastChannel
      this.broadcastChannel.postMessage(message);
      console.log(`📤 OfflineSocket: Emitted "${event}" via BroadcastChannel`, data);
    } else {
      // Use localStorage
      localStorage.setItem('offline-socket-message', JSON.stringify(message));
      // Clear after a short delay to allow other tabs to read
      setTimeout(() => {
        if (localStorage.getItem('offline-socket-message') === JSON.stringify(message)) {
          localStorage.removeItem('offline-socket-message');
        }
      }, 100);
      console.log(`📤 OfflineSocket: Emitted "${event}" via localStorage`, data);
    }
  }

  /**
   * Handle incoming message from other tabs
   */
  handleMessage(message) {
    // Don't process our own messages
    if (message.socketId === this.socketId) {
      return;
    }

    const { event, data } = message;
    
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
          console.log(`📥 OfflineSocket: Received "${event}"`, data);
        } catch (err) {
          console.error(`OfflineSocket: Error in "${event}" callback`, err);
        }
      });
    }
  }

  /**
   * Set online/offline status
   */
  setOnlineStatus(isOnline) {
    this.isOnline = isOnline;
    console.log(`🔄 OfflineSocket: Status changed to ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
  }

  /**
   * Clean up
   */
  disconnect() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    this.listeners.clear();
    console.log('🔌 OfflineSocket: Disconnected');
  }
}

// Export singleton instance
const offlineSocket = new OfflineSocket();
export default offlineSocket;
